import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, CircularProgress, Paper, ButtonGroup } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { appStore } from '../../zustand';
import { handleError } from '../../util/functions';
import { isEmpty } from 'underscore';
import { makeRequest } from '../../util/AxiosInstance';
import { Chat, EmailOutlined } from '@mui/icons-material';

const PaymentStatus = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [verificationState, setVerificationState] = useState('loading');
    const [errorMessage, setErrorMessage] = useState('An unexpected error occurred.');
    const mode = appStore((state) => state.mode);

    const status = searchParams.get('status');
    const tx_ref = searchParams.get('tx_ref');

    useEffect(() => {
        if (status !== 'successful' || isEmpty(tx_ref)) {
            setVerificationState('failed_initial');
            setErrorMessage('Invalid payment status or missing transaction reference. Please ensure you followed the correct payment link.');
            return;
        }

        setVerificationState('loading');
        const verify = async () => {
            try {
                const response = await makeRequest('GET', `/verify-transaction/${tx_ref}`);

                if (response?.data?.status === true) {
                    setVerificationState('success');
                } else {
                    const apiErrorMessage = handleError(response?.data || 'Verification failed.');
                    setVerificationState('error');
                    setErrorMessage(apiErrorMessage);
                }
            } catch (error) {
                console.error("Verification API error:", error);
                const extractedErrorMessage = handleError(error);
                setVerificationState('error');
                setErrorMessage(extractedErrorMessage);
            }
        };
        verify();

    }, [status, tx_ref]);

    const renderContent = () => {
        switch (verificationState) {
            case 'loading':
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, minHeight: '300px', justifyContent: 'center' }}>
                        <CircularProgress />
                        <Typography>Verifying payment, please wait...</Typography>
                    </Box>
                );
            case 'success':
                return (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.5 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.5 }}
                        style={{ textAlign: 'center' }}
                    >
                        <CheckCircleOutlineIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
                        <Typography variant="h6" gutterBottom>Payment Successful!</Typography>
                        <Typography color={'gray'} variant='body2' sx={{ mb: 3 }}>Your transaction has been verified and your order is updated.</Typography>
                        <Button variant="contained" onClick={() => navigate(mode == 'admin' ? '/' : '/dashboard')}>
                            Go to Dashboard
                        </Button>
                    </motion.div>
                );
            case 'error':
            case 'failed_initial':
                return (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        style={{ textAlign: 'center' }}
                    >
                        <ErrorOutlineIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
                        <Typography variant="h6" gutterBottom>Payment Verification Failed</Typography>
                        <Typography color={'gray'} variant='body2' sx={{ mb: 3 }}>{errorMessage}</Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap', mt: 2, alignItems: 'center', flexDirection: 'column' }}>

                            <Button variant="contained" onClick={() => navigate('/dashboard')}>
                                Go to Dashboard
                            </Button>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                flexDirection: 'column',
                                gap: 2,
                                mt: 2,
                            }}>
                                <ButtonGroup variant="text" aria-label="text button group">
                                    <Button variant="text"
                                        color='secondary'
                                        onClick={() => navigate(mode == 'admin' ? '/chat' : '/dashboard/chat')}
                                        startIcon={<Chat />}
                                    >
                                        Chat
                                    </Button>
                                    <Button variant="text"
                                        color='secondary'
                                        href={`mailto:${import.meta.env.VITE_APP_SUPPORT_EMAIL || 'support@doshby.com'}`}
                                        startIcon={<EmailOutlined />}
                                    >
                                        Send Email
                                    </Button>
                                </ButtonGroup>
                            </Box>
                        </Box>
                    </motion.div>
                );
            default:
                setErrorMessage('An unknown error occurred during payment verification.');
                setVerificationState('error');
                return null;
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 'calc(100vh - 150px)',
                p: 3,
            }}
        >
            <Paper elevation={0} sx={{ p: 4, borderRadius: 2, maxWidth: 'sm', width: '100%' }}>
                <AnimatePresence mode="wait">
                    <motion.div
                        key={verificationState}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        {renderContent()}
                    </motion.div>
                </AnimatePresence>
            </Paper>
        </Box>
    );
};

export default function PaymentPage() {
    return <PaymentStatus />;
}